import { useCallback, useEffect, useMemo, useState } from 'react';
import MartReportsFilterBar, { MartReportSelection } from '../components/mart-reports-filter-bar';
import {
  BetaBreakdownChart,
  BetaBreakdownTable,
  BetaMetricCard,
  BetaMetricCardRow,
  BetaSectionHeading,
  BetaTimeSeriesChart,
} from '../components/beta-charts';
import { BetaFieldToggleSkeleton } from '../components/beta-charts/beta-chart-skeletons';
import {
  useGetMartDomainBreakdownMutation,
  useGetMartGeoBreakdownMutation,
  useGetMartReportMetricsMutation,
  useGetMartTimeSeriesMutation,
} from 'src/redux/admin/mart-reporting/mart-reporting.endpoints';
import { MartReportFilters } from 'src/redux/admin/mart-reporting/mart-reporting.types';
import {
  formatBreakdownFieldLabel,
  formatPlatformLabel,
} from 'src/pages/reports/utils/format-reporting-labels';
import { useReachMetricsPolling } from 'src/pages/reports/hooks/use-reach-metrics-polling';
import {
  isReachComparisonPending,
  shouldShowReachMetricCard,
} from 'src/pages/reports/utils/reach-metric.utils';
import {
  getMetaResultsIndicatorLabel,
  shouldShowResultsMetricCard,
} from 'src/pages/reports/utils/meta-results-metric.utils';

const formatCompactNumber = (value: number, options?: { currency?: boolean; decimals?: number }) => {
  if (options?.currency) {
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: options.decimals ?? 0,
    });
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toLocaleString(undefined, {
    maximumFractionDigits: options?.decimals ?? 0,
  });
};

const formatPercentChange = (current: number, diff: number) => {
  if (!current) return '0%';
  const change = (diff / current) * 100;
  if (!Number.isFinite(change)) return '0%';
  return `${Math.abs(change).toFixed(1)}%`;
};

const getChangeDirection = (diff: number): 'up' | 'down' | 'neutral' => {
  if (diff > 0) return 'up';
  if (diff < 0) return 'down';
  return 'neutral';
};

const ReportingBetaTab = () => {
  const [selection, setSelection] = useState<MartReportSelection | null>(null);
  const [geoField, setGeoField] = useState('country_code');
  const [domainField, setDomainField] = useState('domain');
  const [isReportsLoading, setIsReportsLoading] = useState(true);

  const [fetchMetrics, { data: metricsData }] = useGetMartReportMetricsMutation();
  const [fetchTimeSeries, { data: timeSeriesData = [] }] = useGetMartTimeSeriesMutation();
  const [fetchGeo, { data: geoData = [] }] = useGetMartGeoBreakdownMutation();
  const [fetchDomain, { data: domainData = [] }] = useGetMartDomainBreakdownMutation();

  const requestFilters = useMemo<MartReportFilters | null>(() => {
    if (!selection?.source || !selection.startDate || !selection.endDate) {
      return null;
    }
    return {
      source: selection.source,
      advertiser_ids: selection.advertisers.map((advertiser) => advertiser.id),
      campaign_ids: selection.campaigns.map((campaign) => campaign.id),
      line_item_ids: selection.lineItems.map((lineItem) => lineItem.id),
      start_date: selection.startDate,
      end_date: selection.endDate,
    };
  }, [selection]);

  const loadReports = useCallback(async (signal: { cancelled: boolean }) => {
    if (!requestFilters) {
      setIsReportsLoading(true);
      return;
    }

    setIsReportsLoading(true);
    try {
      await Promise.all([
        fetchMetrics(requestFilters).unwrap(),
        fetchTimeSeries(requestFilters).unwrap(),
        fetchGeo({ ...requestFilters, field: geoField, amount: 10 }).unwrap(),
        fetchDomain({ ...requestFilters, field: domainField, amount: 10 }).unwrap(),
      ]);
    } catch {
      // Keep prior data visible on error; loading state still clears below.
    } finally {
      if (!signal.cancelled) {
        setIsReportsLoading(false);
      }
    }
  }, [requestFilters, geoField, domainField, fetchMetrics, fetchTimeSeries, fetchGeo, fetchDomain]);

  useEffect(() => {
    const signal = { cancelled: false };
    void loadReports(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [loadReports]);

  const refetchMetricsOnly = useCallback(() => {
    if (!requestFilters) return;
    return fetchMetrics(requestFilters).unwrap();
  }, [requestFilters, fetchMetrics]);

  const reachPollingResetKey = requestFilters
    ? `${requestFilters.source}-${requestFilters.start_date}-${requestFilters.end_date}-${requestFilters.advertiser_ids?.join(',')}-${requestFilters.campaign_ids?.join(',')}-${requestFilters.line_item_ids?.join(',')}`
    : '';

  const {
    isReachLoading,
    isReachApproximate,
  } = useReachMetricsPolling({
    reachStatus: metricsData?.current?.reach_status,
    comparisonReachStatus: metricsData?.past?.reach_status,
    resetKey: reachPollingResetKey,
    enabled: Boolean(requestFilters),
    refetch: refetchMetricsOnly,
  });

  const showReportSkeletons = isReportsLoading || !requestFilters;

  const sparklineByMetric = useMemo(() => {
    const build = (metric: keyof typeof timeSeriesData[number]) =>
      timeSeriesData.map((point) => ({ value: Number(point[metric] ?? 0) }));
    return {
      clicks: build('clicks'),
      conversions: build('conversions'),
      impressions: build('impressions'),
      spend: build('spend'),
      reach: build('reach'),
      results: build('results'),
    };
  }, [timeSeriesData]);

  const shouldShowReach = useMemo(
    () => shouldShowReachMetricCard(
      metricsData?.current?.reach,
      metricsData?.current?.reach_status,
    ),
    [metricsData],
  );

  const suppressReachDifference = isReachComparisonPending(
    metricsData?.current?.reach_status,
    metricsData?.past?.reach_status,
  );

  const shouldShowResults = useMemo(
    () => shouldShowResultsMetricCard(metricsData?.current),
    [metricsData],
  );

  const resultsMixed = Boolean(metricsData?.current?.results_mixed);
  const resultsIndicatorLabel = getMetaResultsIndicatorLabel(metricsData?.current);

  const subtitle = selection
    ? `${formatPlatformLabel(selection.source)} · ${selection.startDate} to ${selection.endDate}`
    : undefined;

  return (
    <div className="h-full overflow-y-auto bg-[#F4F5F8] px-6 py-6 sm:px-8">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-bold leading-tight tracking-[-0.6px] text-[#0D0F1A]">
            Reporting (Beta)
          </h1>
          <p className="mt-1 text-[13px] text-[#5E6278]">
            Unified mart reporting across platforms (admin preview)
          </p>
        </div>
        <span className="mt-0.5 box-border inline-flex h-6 shrink-0 items-center justify-center rounded-full border border-[#D9DBE3] bg-white px-3 text-[11px] font-bold uppercase leading-none tracking-[0.06em] text-[#5E6278]">
          Beta
        </span>
      </div>

      <MartReportsFilterBar onSelectionChange={setSelection} />

      <BetaSectionHeading>KPI Overview</BetaSectionHeading>
      <BetaMetricCardRow>
        <BetaMetricCard
          label="Clicks"
          variant="clicks"
          isLoading={showReportSkeletons}
          value={formatCompactNumber(metricsData?.current?.clicks ?? 0)}
          changeLabel={formatPercentChange(
            metricsData?.current?.clicks ?? 0,
            metricsData?.dif?.clicks ?? 0,
          )}
          changeDirection={getChangeDirection(metricsData?.dif?.clicks ?? 0)}
          sparklineData={sparklineByMetric.clicks}
        />
        <BetaMetricCard
          label="Conversions"
          variant="conversions"
          isLoading={showReportSkeletons}
          value={formatCompactNumber(metricsData?.current?.conversions ?? 0)}
          changeLabel={formatPercentChange(
            metricsData?.current?.conversions ?? 0,
            metricsData?.dif?.conversions ?? 0,
          )}
          changeDirection={getChangeDirection(metricsData?.dif?.conversions ?? 0)}
          sparklineData={sparklineByMetric.conversions}
        />
        <BetaMetricCard
          label="Impressions"
          variant="impressions"
          isLoading={showReportSkeletons}
          value={formatCompactNumber(metricsData?.current?.impressions ?? 0)}
          changeLabel={formatPercentChange(
            metricsData?.current?.impressions ?? 0,
            metricsData?.dif?.impressions ?? 0,
          )}
          changeDirection={getChangeDirection(metricsData?.dif?.impressions ?? 0)}
          sparklineData={sparklineByMetric.impressions}
        />
        {shouldShowReach ? (
          <BetaMetricCard
            label="Reach"
            variant="reach"
            isLoading={showReportSkeletons || isReachLoading}
            isApproximate={isReachApproximate}
            value={formatCompactNumber(metricsData?.current?.reach ?? 0)}
            changeLabel={
              suppressReachDifference
                ? undefined
                : formatPercentChange(
                    metricsData?.current?.reach ?? 0,
                    metricsData?.dif?.reach ?? 0,
                  )
            }
            changeDirection={
              suppressReachDifference
                ? 'neutral'
                : getChangeDirection(metricsData?.dif?.reach ?? 0)
            }
            sparklineData={sparklineByMetric.reach}
          />
        ) : null}
        {shouldShowResults ? (
          <BetaMetricCard
            label="Results"
            variant="results"
            isLoading={showReportSkeletons}
            indicatorLabel={resultsIndicatorLabel}
            value={
              resultsMixed
                ? '—'
                : formatCompactNumber(metricsData?.current?.results ?? 0)
            }
            changeLabel={
              resultsMixed
                ? undefined
                : formatPercentChange(
                    metricsData?.current?.results ?? 0,
                    metricsData?.dif?.results ?? 0,
                  )
            }
            changeDirection={
              resultsMixed
                ? 'neutral'
                : getChangeDirection(metricsData?.dif?.results ?? 0)
            }
            sparklineData={sparklineByMetric.results}
          />
        ) : null}
        <BetaMetricCard
          label="Spend"
          variant="spend"
          isLoading={showReportSkeletons}
          value={formatCompactNumber(metricsData?.current?.spend ?? 0, { currency: true })}
          changeLabel={formatPercentChange(
            metricsData?.current?.spend ?? 0,
            metricsData?.dif?.spend ?? 0,
          )}
          changeDirection={getChangeDirection(metricsData?.dif?.spend ?? 0)}
          sparklineData={sparklineByMetric.spend}
        />
      </BetaMetricCardRow>

      <BetaSectionHeading>Performance Over Time</BetaSectionHeading>
      <BetaTimeSeriesChart
        data={timeSeriesData}
        subtitle={subtitle}
        isLoading={showReportSkeletons}
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <div>
          <BetaSectionHeading>Geo Breakdown</BetaSectionHeading>
          <div className="mb-3">
            {showReportSkeletons ? (
              <BetaFieldToggleSkeleton />
            ) : (
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'country_code', label: 'Country' },
                  { id: 'region', label: 'Region' },
                  { id: 'city', label: 'City' },
                  { id: 'dma', label: 'DMA' },
                  { id: 'zipcode', label: 'Zip' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`rounded-md px-3 py-1.5 text-[11.5px] font-semibold ${
                      geoField === option.id
                        ? 'bg-white text-[#0D0F1A] shadow-[0_1px_2px_rgba(13,15,26,0.05)]'
                        : 'bg-[#EDEEF2] text-[#5E6278]'
                    }`}
                    onClick={() => setGeoField(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <BetaBreakdownChart
            title="Top Locations"
            subtitle={`By ${formatBreakdownFieldLabel('geo', geoField)}`}
            breakdownField={geoField}
            breakdownKind="geo"
            data={geoData}
            colour="#059669"
            isLoading={showReportSkeletons}
          />
          <BetaBreakdownTable
            title="Geo Table"
            subtitle="Impressions by location"
            breakdownField={geoField}
            breakdownKind="geo"
            data={geoData}
            colour="#059669"
            isLoading={showReportSkeletons}
          />
        </div>

        <div>
          <BetaSectionHeading>Domain / App Breakdown</BetaSectionHeading>
          <div className="mb-3">
            {showReportSkeletons ? (
              <BetaFieldToggleSkeleton />
            ) : (
              <div className="flex flex-wrap gap-2">
                {[
                  { id: 'domain', label: 'Domain' },
                  { id: 'app', label: 'App' },
                  { id: 'app_name', label: 'App Name' },
                  { id: 'inventory_type', label: 'Inventory' },
                ].map((option) => (
                  <button
                    key={option.id}
                    type="button"
                    className={`rounded-md px-3 py-1.5 text-[11.5px] font-semibold ${
                      domainField === option.id
                        ? 'bg-white text-[#0D0F1A] shadow-[0_1px_2px_rgba(13,15,26,0.05)]'
                        : 'bg-[#EDEEF2] text-[#5E6278]'
                    }`}
                    onClick={() => setDomainField(option.id)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <BetaBreakdownChart
            title="Top Domains / Apps"
            subtitle={`By ${formatBreakdownFieldLabel('domain', domainField)}`}
            breakdownField={domainField}
            breakdownKind="domain"
            data={domainData}
            colour="#2563EB"
            isLoading={showReportSkeletons}
          />
          <BetaBreakdownTable
            title="Domain Table"
            subtitle="Impressions by domain or app"
            breakdownField={domainField}
            breakdownKind="domain"
            data={domainData}
            colour="#2563EB"
            isLoading={showReportSkeletons}
          />
        </div>
      </div>
    </div>
  );
};

export default ReportingBetaTab;
